import React from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const defaultImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJsb2cgSW1hZ2U8L3RleHQ+PC9zdmc+';

const categoryToSlug = (cat) =>
  (cat || '').toLowerCase().replace(/&/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const BlogCard = ({ blog, featured = false }) => {
  const {
    title,
    slug,
    type = 'blog',
    excerpt,
    category,
    // eslint-disable-next-line no-unused-vars
    tags = [],
    featuredImage,
    authorName,
    publishedAt,
    readTime,
    viewCount,
    commentCount,
  } = blog;

  const imageUrl = featuredImage?.fileId
    ? `${API_URL}/api/upload/file/${featuredImage.fileId}`
    : defaultImage;

  const blogUrl = `/${type}/${categoryToSlug(category)}/${slug}`;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateExcerpt = (text, maxLength = 120) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const handleClick = () => window.scrollTo(10, 0);

  return (
    <article
      className={`blog_card ${featured ? 'blog_card--featured' : ''}`}
      itemScope
      itemType="https://schema.org/BlogPosting"
    >
      {/* Image */}
      <Link
        to={blogUrl}
        className="blog_card__image"
        onClick={handleClick}
        aria-label={`Read more about ${title}`}
      >
        <img
          src={imageUrl}
          alt={title}
          loading="lazy"
          itemProp="image"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="blog_card__image_overlay">
          <span className="read_more_icon">
            <i className="fa-solid fa-arrow-up-right"></i>
          </span>
        </div>
      </Link>

      {/* Content */}
      <div className="blog_card__content">
        {/* Meta Top */}
        <div className="blog_card__meta_top">
          {category && (
            <Link
              to={`/blog?category=${encodeURIComponent(category)}`}
              className="blog_card__category"
              onClick={handleClick}
              itemProp="articleSection"
            >
              {category}
            </Link>
          )}
          <span className="blog_card__read_time">
            <i className="fa-regular fa-clock"></i>
            <span>{readTime || 5} min read</span>
          </span>
        </div>

        {/* Title */}
        <h3 className="blog_card__title" itemProp="headline">
          <Link to={blogUrl} onClick={handleClick}>
            {title}
          </Link>
        </h3>

        {/* Excerpt */}
        <p className="blog_card__excerpt" itemProp="description">
          {truncateExcerpt(excerpt, featured ? 200 : 120)}
        </p>

        {/* Meta Bottom */}
        <div className="blog_card__meta_bottom">
          <div className="blog_card__author">
            <div className="blog_card__author_avatar">
              {authorName?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="blog_card__author_info">
              <span className="author_name" itemProp="author" itemScope itemType="https://schema.org/Person">
                <span itemProp="name">{authorName || 'Admin'}</span>
              </span>
              <time className="publish_date" dateTime={publishedAt} itemProp="datePublished">
                {formatDate(publishedAt)}
              </time>
            </div>
          </div>

          <div className="blog_card__stats">
            {viewCount > 0 && (
              <span className="blog_card__views" title={`${viewCount} views`}>
                <i className="fa-regular fa-eye"></i>
                <span>{viewCount >= 1000 ? `${(viewCount / 1000).toFixed(1)}k` : viewCount}</span>
              </span>
            )}
            {commentCount > 0 && (
              <span className="blog_card__views" title={`${commentCount} comments`}>
                <i className="fa-regular fa-comments"></i>
                <span>{commentCount}</span>
              </span>
            )}
            <Link
              to={blogUrl}
              className="blog_card__read_btn"
              onClick={handleClick}
              aria-label={`Read full article: ${title}`}
            >
              Read More
              <i className="fa-solid fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>

      <meta itemProp="url" content={`${window.location.origin}${blogUrl}`} />
    </article>
  );
};

export default BlogCard;
